import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import ColorService from "../../services/ColorService";
import WordScoreCard from "./WordScoreCard";

const LEVEL_COLORS = {
    HARD: () => ColorService.getDifficultyColor("HARD"),
    MEDIUM: () => ColorService.getDifficultyColor("MEDIUM"),
    EASY: () => ColorService.getDifficultyColor("EASY"),
    EXCLUDED: () => "#ccc",
    NO_SCORE: () => "#ccc",
    ALL: () => "#ccc",
};

/**
 * Section displaying a titled list of word score cards. Title and card border use the level's color.
 * @param {"HARD"|"MEDIUM"|"EASY"|"EXCLUDED"|"NO_SCORE"|"ALL"} level - Level for title color and card border color
 * @param {string} title - Section title
 * @param {Array<{ id?: string, word: string, info?: { score?: number | null } }>} wordDataList - List of word data for cards
 * @param {React.ReactNode} [caption] - Optional caption below title, above divider
 */
export default function WordScoreSection({
    level,
    title,
    wordDataList = [],
    caption,
}) {
    if (!wordDataList || wordDataList.length === 0) return null;

    const getColor = LEVEL_COLORS[level] || (() => "#ccc");
    const color = getColor();
    const titleColor = ["HARD", "MEDIUM", "EASY"].includes(level) ? color : "text.secondary";
    const borderColor = color;

    return (
        <Box>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: "bold",
                    mb: 1,
                    color: titleColor,
                }}
            >
                {title}
            </Typography>
            {caption && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                >
                    {caption}
                </Typography>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(6, 1fr)" },
                    gap: "16px",
                }}
            >
                {wordDataList.map((wordData) => (
                    <Box key={wordData.id || wordData.word} sx={{ minWidth: 0 }}>
                        <WordScoreCard
                            word={wordData.word}
                            score={wordData.info?.score}
                            borderColor={borderColor}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
